import React, { useMemo } from "react";

import { Redirect, useParams } from "react-router-dom";

import { SharedResource } from "@fartherfinance/frontend/api/Sharing/Types";
import useGetTransitionProposals from "@fartherfinance/frontend/api/TransitionProposals/hooks/useGetTransitionProposals";
import { TransitionProposalId } from "@fartherfinance/frontend/api/TransitionProposals/types";
import { ClientId } from "@fartherfinance/frontend/api/Types";

import { importTypes } from "../../constants";
import { TransitionProposalFlowStateData } from "../../types";
import useRequestAuth from "@src/multiCustodian/hooks/useRequestAuth";
import DocumentsCenter from "@src/multiCustodian/pages/Client/:clientId/DocumentsCenter";
import { actions } from "@src/sharedComponents/FlowContainer/reducer/actions";
import { useFlowContainerContext } from "@src/sharedComponents/FlowContainer/reducer/Context";
import LogoLoadingStill from "@src/sharedComponents/LogoLoadingStill/LogoLoadingStill";
import SelectAccounts from "@src/sharedComponents/SelectAccounts/SelectAccounts";
import SelectAccountsForm from "@src/sharedComponents/SelectAccounts/SelectAccountsForm";

const Add = () => {
  const { clientId, transitionProposalId } = useParams<{
    clientId: ClientId;
    transitionProposalId: TransitionProposalId;
  }>();

  const auth = useRequestAuth();

  const {
    data: transitionProposalsResponse,
    isLoading: isFetchingTransitionProposals,
  } = useGetTransitionProposals({ clientId }, auth);

  const transitionProposal = useMemo(() => {
    const transitionProposals = transitionProposalsResponse?.data;

    if (transitionProposals === undefined) {
      return undefined;
    }

    return transitionProposals?.find(
      (t) => t.transitionProposalId === transitionProposalId
    );
  }, [transitionProposalsResponse, transitionProposalId]);

  const {
    state: { data },
    dispatch,
  } = useFlowContainerContext<Partial<TransitionProposalFlowStateData>>();

  const resources = useMemo(() => {
    if (data.resources !== undefined) {
      return data.resources;
    }
    return [];
  }, [data.resources]);

  const handleToggleSelectAll = (
    isChecked: boolean,
    allResources: SharedResource[]
  ) => {
    if (isChecked) {
      dispatch({
        type: actions.SET_DATA,
        payload: { resources: [...allResources] },
      });
    } else {
      dispatch({ type: actions.SET_DATA, payload: { resources: [] } });
    }
  };

  const handleOnToggleCheckBox = (
    isChecked: boolean,
    resource: SharedResource
  ) => {
    if (isChecked) {
      dispatch({
        type: actions.SET_DATA,
        payload: { resources: [...resources, resource] },
      });
    } else {
      dispatch({
        type: actions.SET_DATA,
        payload: {
          resources: resources.filter(
            (r) => r.resourceId !== resource.resourceId
          ),
        },
      });
    }
  };

  if (isFetchingTransitionProposals || transitionProposal === undefined) {
    return <LogoLoadingStill onTop />;
  }

  switch (transitionProposal.importType) {
    case importTypes.STATEMENT:
      return (
        <DocumentsCenter
          basePath={`/Client/${clientId}/Investments/TransitionProposals/${transitionProposalId}`}
          showSideNav={false}
          isCheckBoxTable
        />
      );
    case importTypes.EXISTING:
      return (
        <SelectAccounts showSelectedResourcesDefault>
          <SelectAccountsForm
            selectedResources={resources}
            onToggleSelectAll={handleToggleSelectAll}
            onToggleCheckBox={handleOnToggleCheckBox}
            includeExternalAccounts={false}
            includeManualAccounts={false}
          />
        </SelectAccounts>
      );
    case importTypes.MANUAL:
      return <div>Manual</div>;
    default:
      return (
        <Redirect to={`/Client/${clientId}/Investments/TransitionProposals`} />
      );
  }
};

export default Add;
